import { OPTIMIZATION } from 'constants/report';

import * as React from 'react';
import { useState } from 'react';

import { useQuery } from '@apollo/client';
import ReportGgVarTuningPlot from '@graphql/queries/ReportGgVarTuningPlot';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import FlavorStructureDiagramIcon from 'components/FlavorStructureDiagram/FlavorStructureDiagramIcon';
import FlavorStructureDiagramModal from 'components/FlavorStructureDiagram/FlavorStructureDiagramModal';
import SvgFileIcon from 'components/Icons/DownloadIcons/SvgFileIcon';
import LoadingScreen from 'components/LoadingScreen';
import GGVarImpact from 'components/Report/GGVarImpact';
import PqIntensityDiagram from 'components/Report/PqIntensityDiagram';
import { ProductVersion } from 'components/Report/ProductVersion';
import { getProductName } from 'components/Report/utils';
import DOMPurify from 'dompurify';
import useCustomProductNames from 'hooks/useCustomProductNames';
import * as htmlToImage from 'html-to-image';
import { useTranslation } from 'react-i18next';
import { tuningPlotService } from 'services/tuningPlotService';
import { ExtremaRow } from 'services/utils/tuningPlotUtils';
import { Optional } from 'typescript-optional';

import styles from '../ProjectSummary.module.css';

export const mostPositiveWhenIncreased = (
  input: reports.ExtremaData[],
): reports.ExtremaData => {
  return input
    .filter(
      (ed) =>
        ed.maxCategory === '+' ||
        ed.maxCategory === '++' ||
        ed.maxCategory === '+++',
    )
    .reduce((a, b) => (a && a.maxExtrema >= b.maxExtrema ? a : b), null);
};

export const mostPositiveWhenDecreased = (
  input: reports.ExtremaData[],
): reports.ExtremaData => {
  return input
    .filter(
      (ed) =>
        ed.minCategory === '+' ||
        ed.minCategory === '++' ||
        ed.minCategory === '+++',
    )
    .reduce((a, b) => (a && a.minExtrema >= b.minExtrema ? a : b), null);
};

interface Props {
  projectId: number;
  projectReports: reports.ReportProductInfo[];
  selectedReportId: string;
  product?: ProductVersion;
}

const meanProduct: ProductVersion = new ProductVersion(null, null);

function getSelectedReport(
  projectReports: reports.ReportProductInfo[],
  selectedReportId: string,
) {
  return Optional.of(projectReports).map((pr) =>
    pr.find((r) => r.reportId === selectedReportId),
  );
}

function getProducts(
  projectReports: reports.ReportProductInfo[],
  selectedReportId: string,
) {
  return getSelectedReport(projectReports, selectedReportId)
    .map((report) => {
      if (report.reportType !== OPTIMIZATION) {
        return [meanProduct].concat(
          report.products.map((p) => new ProductVersion(p.id, p.version)),
        );
      } else {
        return report.products.map((p) => new ProductVersion(p.id, p.version));
      }
    })
    .orElse([]);
}

function getDefaultProduct(
  projectReports: reports.ReportProductInfo[],
  selectedReportId: string,
  product: ProductVersion,
) {
  return getSelectedReport(projectReports, selectedReportId)
    .map((report) => {
      if (report.reportType !== OPTIMIZATION) {
        return product || meanProduct;
      } else {
        if (product.equals(meanProduct)) {
          return getProducts(projectReports, selectedReportId).find(
            (p) => p.version === null,
          );
        } else {
          return product;
        }
      }
    })
    .orElse(null);
}

/**
 * The data URI produced by html-to-image embeds a serialized snapshot of a
 * DOM subtree inside a single SVG `<foreignObject>`. Since that subtree can
 * contain dynamic content, sanitize the embedded markup before it is used
 * as a download link to prevent DOM XSS via the exported SVG.
 *
 * The foreignObject's markup is located and sanitized as a plain string
 * (rather than round-tripped through DOMParser/XMLSerializer on the XML
 * SVG document) because DOMPurify serializes using HTML rules while an
 * XML document requires strict XHTML - reassigning DOMPurify's HTML output
 * back into `innerHTML` on an XML-parsed element throws for content
 * containing HTML void elements (e.g. `<br>`, `<img>`).
 */
function sanitizeSvgDataUrl(dataUrl: string): string {
  const match = dataUrl.match(/^data:image\/svg\+xml;charset=utf-8,(.*)$/s);
  if (!match) {
    console.error('Unexpected SVG export format, aborting download');
    return '';
  }

  const svgMarkup = decodeURIComponent(match[1]);
  const openTagMatch = svgMarkup.match(/<foreignObject\b[^>]*>/i);
  const closeTagIndex = svgMarkup.lastIndexOf('</foreignObject>');
  if (!openTagMatch || closeTagIndex === -1) {
    console.error('Could not locate exported SVG content, aborting download');
    return '';
  }

  const contentStart = openTagMatch.index + openTagMatch[0].length;
  const beforeContent = svgMarkup.slice(0, contentStart);
  const innerContent = svgMarkup.slice(contentStart, closeTagIndex);
  const afterContent = svgMarkup.slice(closeTagIndex);
  const sanitizedInnerContent = DOMPurify.sanitize(innerContent);
  const sanitizedMarkup = `${beforeContent}${sanitizedInnerContent}${afterContent}`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    sanitizedMarkup,
  )}`;
}

const ProjectFlavorSummary: React.FC<Props> = (props) => {
  const { projectReports, selectedReportId, projectId, product } = props;

  const [selectedProduct, setSelectedProduct] = useState<ProductVersion>(
    getDefaultProduct(projectReports, selectedReportId, product),
  );
  const [isLocal, setIsLocal] = useState<boolean>(false);
  const [showFSDModal, setShowFSDModal] = useState<boolean>(false);

  const { t } = useTranslation();
  const customProductNames = useCustomProductNames({
    projectId,
    reportId: selectedReportId,
  });

  const { data, loading, error } = useQuery<reports.GgVarTuningPlotResponse>(
    ReportGgVarTuningPlot,
    {
      variables: {
        reportID: selectedReportId,
        productID: selectedProduct.productId,
      },
    },
  );

  const enrichData = (row: ExtremaRow): reports.ExtremaData => ({
    ggvar: row.ggvar,
    productId: row.productId,
    version: row.version,
    productName: getProductName({
      productNames: customProductNames,
      productId: selectedProduct.productId,
      version: selectedProduct.version,
    }),
    projectId: projectId,
    reportId: row.reportId,
    minExtrema: row.minExtrema,
    maxExtrema: row.maxExtrema,
    minCategory: tuningPlotService.categorizeExtrema(row.minExtrema),
    maxCategory: tuningPlotService.categorizeExtrema(row.maxExtrema),
  });

  const downloadAsSvg = () => {
    const doc = document.getElementById('pqDiagramDivId');
    const anchorTags = doc.querySelectorAll('a');
    anchorTags.forEach((anchor) => {
      const innerContent = anchor.querySelector('span');
      if (anchor.parentNode && anchor.parentNode.replaceChild)
        anchor.parentNode.replaceChild(innerContent, anchor);
    });

    htmlToImage.toSvg(doc).then((dataUrl) => {
      // The generated SVG data URI embeds a serialized snapshot of this
      // DOM subtree (via an inner <foreignObject>), which may include
      // dynamic content such as product/report labels. Sanitize it before
      // it is used as a download link to prevent DOM XSS via the exported
      // markup.
      const sanitizedDataUrl = sanitizeSvgDataUrl(dataUrl);
      if (!sanitizedDataUrl) {
        return;
      }
      const link = document.createElement('a');
      link.href = DOMPurify.sanitize(sanitizedDataUrl);
      link.download = 'pq-intensity.svg';
      link.click();
    });
  };

  if (loading || error) {
    return <LoadingScreen />;
  }

  return (
    <div className={styles.tabContainer}>
      {projectReports && (
        <Grid container spacing={2}>
          <Grid item xs={2}>
            {!meanProduct.equals(selectedProduct) && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  id="svg-link"
                  size="small"
                  onClick={() => setShowFSDModal(true)}
                >
                  <FlavorStructureDiagramIcon fontSize="large" />
                </IconButton>
                <FlavorStructureDiagramModal
                  showFSDModal={showFSDModal}
                  setShowFSDModal={setShowFSDModal}
                  reportId={selectedReportId}
                  productId={selectedProduct.productId}
                  productName={getProductName({
                    productNames: customProductNames,
                    productId: selectedProduct.productId,
                    version: selectedProduct.version,
                  })}
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={6}>
            <div className={styles.selectProductView}>
              <div className={styles.select}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel id="report-id-select-label">
                    {t('flavorSummary.product')}
                  </InputLabel>
                  <Select
                    variant="standard"
                    id="product-id-select"
                    labelId="product-id-select-label"
                    value={JSON.stringify(selectedProduct)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setSelectedProduct(JSON.parse(event.target.value))
                    }
                  >
                    {getProducts(projectReports, selectedReportId).map(
                      (product) => (
                        <MenuItem
                          key={product.value}
                          value={JSON.stringify(product)}
                        >
                          {product.productId
                            ? getProductName({
                                productNames: customProductNames,
                                productId: product.productId,
                                version: product.version,
                              })
                            : 'Mean Product'}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
              </div>
            </div>
          </Grid>
          <Grid item xs={3}>
            <FormGroup row>
              <Tooltip title={t('flavorSummary.localOnlyTooltip')}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isLocal}
                      onChange={(e) => setIsLocal(e.target.checked)}
                      name="isLocal"
                    />
                  }
                  label={
                    <Typography variant="caption">
                      {t('flavorSummary.localOnly')}
                    </Typography>
                  }
                />
              </Tooltip>
              <IconButton id="svg-link" size="small" onClick={downloadAsSvg}>
                <SvgFileIcon fontSize="large" />
              </IconButton>
            </FormGroup>
          </Grid>
          <Grid item xs={12} id="pqDiagramDivId">
            <Grid item xs={12}>
              {projectReports && (
                <div>
                  <PqIntensityDiagram
                    extremaData={tuningPlotService
                      .extremaData(data, selectedProduct, isLocal)
                      .map((row) => enrichData(row))}
                  />
                </div>
              )}
            </Grid>
            <Grid item xs={12}>
              <Grid container justifyContent="center" alignItems="center">
                <Grid item xs={3}>
                  <GGVarImpact
                    type="increased"
                    extrema={mostPositiveWhenIncreased(
                      tuningPlotService
                        .extremaData(data, selectedProduct, isLocal)
                        .map((row) => enrichData(row)),
                    )}
                  />
                </Grid>
                <Grid item xs={2}></Grid>
                <Grid item xs={3}>
                  <GGVarImpact
                    type="decreased"
                    extrema={mostPositiveWhenDecreased(
                      tuningPlotService
                        .extremaData(data, selectedProduct, isLocal)
                        .map((row) => enrichData(row)),
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default ProjectFlavorSummary;
